import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UserStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { Navigate } from 'react-router-dom';

import FocusLayout from 'layouts/FocusLayout';

import ErrorMessage from 'components/ErrorMessage';

import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';

import { logout } from 'utils/auth';

import NextSteps from './NextSteps';
import ReportAccountSuspension from './ReportAccountSuspension';

const UNDER_REVIEW_USER_QUERY = gql(`
  query UnderReviewUser {
    dashboard {
      id
      user {
        id
        status
      }
    }
  }
`);

const UnderReviewUser: FC = () => {
  const { loading, error, data, refetch } = useQuery(UNDER_REVIEW_USER_QUERY);

  if (loading) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const user = data.dashboard.user;

  if (user.status !== UserStatusEnumType.UnderReview) {
    return <Navigate to="/" />;
  }

  return (
    <FocusLayout>
      <main className="grid min-h-full place-items-center py-32">
        <div className="text-center">
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Your account is <span className="text-orange-600">under review</span>
          </h1>
          <p className="mt-6 text-base leading-7 text-gray-600">
            We have received your application and are reviewing your information. This process
            typically takes 1-2 business days. If you have any questions, please contact us at{' '}
            <a href="mailto:hi@angellistindia.com" className="text-indigo-600">
              hi@angellistindia.com
            </a>
            .
          </p>
          <NextSteps />
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <ConfirmButton
              className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onConfirm={logout}
              title="Confirm logout"
              description="Are you sure you want to logout?"
              variant="secondary"
            >
              Logout
            </ConfirmButton>
            <ReportAccountSuspension />
          </div>
        </div>
      </main>
    </FocusLayout>
  );
};

export default UnderReviewUser;
